import { getConfig } from '../../configs/config';
import messages from '../../constants/logMessages.json';
import tracking from '../../tracking/tracking';
import {
  AnalyticsEventTypes,
  AnalyticsPageView,
  TransactionComplete,
  TransactionStart,
} from '../../types';
import logging, { getMessage } from '../logging/index';
import tools from '../tools/index';
import {
  completeTimeHandler,
  // CompletionEvents,
} from './startCompleteTimeHelper';
import { mergeInitialData, omitBy } from './utilHelpers';

const { infoHeading, warnHeading, infoBold, warnBold } = messages;

/**
 * Handles transaction milestones triggered either by direct calls or KPIHelpers.
 * This function checks transaction type and completion status,
 * sanitizes transaction type, checks for deduplication of milestones,
 * and manages milestone persistence based on event types and configuration.
 *
 * @param {TransactionStart | TransactionComplete} options Object containing event type and data for the transaction.
 * @returns {boolean} Returns true if the transaction milestone is handled successfully, otherwise false.
 */

export const transactionMilestoneHandler = ({
  event = AnalyticsEventTypes.TransactionStart,
  data = { transactionType: '' },
}: TransactionStart | TransactionComplete) => {
  const { transactionType } = data;
  const { enableMilestonesPersist } = getConfig();
  const analyticsMilestones =
    tools.handlePersistedConfig()?.analyticsMilestones || {};

  // Check whether transaction type is available.
  if (!transactionType) {
    logging.info(
      getMessage('noTransactionType', [event]),
      warnHeading,
      warnBold
    );
    return false;
  }

  const sanitisedTransactionType = transactionType
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (match: string, index: number) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase()
    )
    .replace(/\s+/g, ''); // Remove spaces

  const transactionMilestoneType = `${sanitisedTransactionType}${
    event === AnalyticsEventTypes.TransactionStart ? 'Start' : 'Complete'
  }`;

  // Check whether transaction milestone has previously fired.
  if (
    transactionMilestoneType &&
    analyticsMilestones[transactionMilestoneType]
  ) {
    logging.info(
      getMessage('transactionDeduplicated', [
        event,
        sanitisedTransactionType,
        transactionMilestoneType,
      ]),
      infoHeading,
      infoBold
    );
    return false;
  }

  const startMilestoneKey = `${sanitisedTransactionType}Start`;
  const completeMilestoneKey = `${sanitisedTransactionType}Complete`;
  const milestones = tools.getAllMilestonesFired?.();
  const { [startMilestoneKey]: startMilestone } = milestones;

  // Transaction Start event
  if (event === AnalyticsEventTypes.TransactionStart) {
    tools.setMilestone?.(startMilestoneKey);
    return true;
  }
  // Transaction Complete event
  if (event === AnalyticsEventTypes.TransactionComplete) {
    // Dedupe Logic - if no transaction_start then return early
    if (!startMilestone) {
      logging.info(
        getMessage('transactionStartNotFound', [sanitisedTransactionType]),
        warnHeading,
        warnBold
      );
      return false;
    }

    // Persist Transaction Milestone when enableMilestonesPersist true.
    if (enableMilestonesPersist) {
      tools.setMilestone?.(completeMilestoneKey);
    } else {
      tools.resetMilestone?.(startMilestoneKey);
      tools.resetMilestone?.(completeMilestoneKey);
    }
  }

  return true;
};

/**
 * Handles the analytics for transaction milestones triggered by page views.
 * This function is called from the Pageview via KPI helper.
 * It extracts transaction type and completion status from the payload,
 * generates a tracking payload, sanitizes the transaction type,
 * merges data, triggers analytics custom event,
 * and persists transaction milestones if enabled.
 *
 * @param {AnalyticsPageView} payload The payload containing analytics data for the page view.
 */
export const transactionMilestonePageViewHandler = (
  payload: AnalyticsPageView
): void => {
  const { data: { transactionType = '', transactionComplete = false } = {} } =
    payload;

  // Terminate Early Non transactionComplete
  if (!transactionComplete) return;

  // Show warning message and terminate early with no transaction type data point
  if (!transactionType) {
    logging.info(
      getMessage('noTransactionType', [
        AnalyticsEventTypes.TransactionComplete,
      ]),
      warnHeading,
      warnBold
    );
    return;
  }

  const { appComplete, ...restData } = payload.data ?? {};

  // Valid conditions with transactionComplete and transaction type.
  const trackingPayload: TransactionComplete = {
    event: AnalyticsEventTypes.TransactionComplete,
    data: {
      ...restData,
      transactionType,
      cm_transaction_complete: 1,
      ...(appComplete === true ? { appComplete: true } : {}),
    },
  };

  // Validation - checking whether milestone has previous fired.
  if (!transactionMilestoneHandler(trackingPayload)) return;
  const mergedData = omitBy(
    mergeInitialData(completeTimeHandler(trackingPayload))
  );
  tracking.analyticsCustomEvent(mergedData);
};
